import type { JSX } from "react";
import { useMemo } from "react";

import { formatFileNameForExport } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import * as XLSXUtils from "@impero-com/common/utils/xlsx";
import {
    TableToolbar,
    TextTool,
    ToolbarSpacer,
    TSTTable,
} from "@impero-com/ui";
import {
    createColumnHelper,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Select } from "antd";
import { Link } from "react-router-dom";
import saveAs from "save-as";

import type { LegacyControlProgramControlSlim } from "api/legacy/model/legacyControlProgramControlSlim";
import type { LegacyControlProgramViewModelWithUserMap } from "api/legacy/model/legacyControlProgramViewModelWithUserMap";
import type { LegacyUserTagCategory } from "api/legacy/model/legacyUserTagCategory";
import { WebRoute } from "routes";
import { newExportMetadata } from "utils/export";

import { aggregateByTagCategory, summarizeAssignees } from "./controlAssignees";
import * as styles from "./ControlProgramDetailPage.css";

interface ControlsTableProps {
    model: LegacyControlProgramViewModelWithUserMap;
    userName: (userId: number | null | undefined) => string;
    categories: LegacyUserTagCategory[];
    selectedCategory: LegacyUserTagCategory;
    onChangeCategory: (categoryId: string) => void;
}

const columnHelper = createColumnHelper<LegacyControlProgramControlSlim>();

export function ControlsTable({
    model,
    userName,
    categories,
    selectedCategory,
    onChangeCategory,
}: ControlsTableProps): JSX.Element {
    const { t } = useTranslation();

    const summaries = useMemo(
        () =>
            new Map(
                model.ControlModels.map((controlModel) => [
                    controlModel.Control.Id,
                    summarizeAssignees(controlModel.Control, userName),
                ])
            ),
        [model, userName]
    );

    const aggregation = useMemo(
        () =>
            aggregateByTagCategory(
                model.ControlModels.map((controlModel) => controlModel.Control),
                selectedCategory,
                userName
            ),
        [model, selectedCategory, userName]
    );

    const columns = useMemo(
        () => [
            columnHelper.accessor(
                (controlModel) => controlModel.Control.Title,
                {
                    id: "title",
                    header: t("ControlProgram_ControlsList_Column_Title"),
                    sortingFn: "text",
                    cell: (info) => (
                        <Link
                            to={`${WebRoute.CONTROLS}/${info.row.original.Control.Id}`}
                        >
                            {info.getValue()}
                        </Link>
                    ),
                }
            ),
            columnHelper.accessor(
                (controlModel) =>
                    summaries.get(controlModel.Control.Id)?.count ?? 0,
                {
                    id: "assignees",
                    header: t("ControlProgram_ControlsList_Column_Assignees"),
                    cell: (info) => (
                        <span
                            title={summaries
                                .get(info.row.original.Control.Id)
                                ?.names.join("\n")}
                        >
                            {info.getValue()}
                        </span>
                    ),
                }
            ),
            ...aggregation.tags.map((tag) =>
                columnHelper.accessor(
                    (controlModel) =>
                        aggregation.aggregates[controlModel.Control.Id]?.[
                            tag.Id ?? ""
                        ]?.assigneeCount ?? 0,
                    {
                        id: `tag-${tag.Id}`,
                        header: tag.Name,
                        cell: (info) => {
                            const aggregate =
                                aggregation.aggregates[
                                    info.row.original.Control.Id
                                ]?.[tag.Id ?? ""];
                            return aggregate ? (
                                <span title={aggregate.assignees.join("\n")}>
                                    {aggregate.assigneeCount}
                                </span>
                            ) : null;
                        },
                    }
                )
            ),
        ],
        [aggregation, summaries, t]
    );

    const table = useReactTable({
        data: model.ControlModels,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        initialState: { sorting: [{ id: "title", desc: false }] },
        enableSortingRemoval: false,
        enableRowSelection: false,
    });

    const handleExport = () => {
        const header = [
            t("ControlProgram_ControlsList_Column_Title"),
            t("ControlProgram_ControlsList_Column_Assignees"),
            ...aggregation.tags.map((tag) => tag.Name),
        ];
        const content = model.ControlModels.map((controlModel) => [
            controlModel.Control.Title,
            summaries.get(controlModel.Control.Id)?.count ?? 0,
            ...aggregation.tags.map(
                (tag) =>
                    aggregation.aggregates[controlModel.Control.Id]?.[
                        tag.Id ?? ""
                    ]?.assigneeCount ?? null
            ),
        ]);
        const columnsWidth = [200, 80, ...aggregation.tags.map(() => 80)];
        const worksheet = XLSXUtils.tableToWorksheet(
            { header, content },
            columnsWidth,
            newExportMetadata()
        );
        const workbook = XLSXUtils.workbookWithSingleWorksheet(
            "Controls",
            worksheet
        );
        const blob = new Blob([XLSXUtils.workbookToBinary(workbook)], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, formatFileNameForExport("controls", "xlsx"));
    };

    return (
        <TSTTable
            table={table}
            noResultsFallback={t("NoResults")}
            noSourceRowsFallback={t("NoResults")}
            toolbar={
                <TableToolbar>
                    <Select
                        className={styles.categorySelect}
                        aria-label={t(
                            "ControlProgram_Controls_List_ViewByCategory"
                        )}
                        value={selectedCategory.Id}
                        onChange={onChangeCategory}
                        showSearch
                        optionFilterProp="label"
                        options={categories.map((category) => ({
                            value: category.Id ?? "",
                            label: category.Name,
                        }))}
                    />
                    <ToolbarSpacer />
                    <TextTool
                        state={{ type: "ENABLED" }}
                        onClick={handleExport}
                    >
                        {t("Export")}
                    </TextTool>
                </TableToolbar>
            }
        />
    );
}
